import { createTheme, useTheme, type Theme } from '@mui/material/styles'

/**
 * One place for the visual language: warm hospitality palettes, generous
 * spacing for dense operational tables, and status colours that read the same
 * on every screen.
 *
 * Three palettes, each in light and dark. Everything a screen might want to
 * paint - chart series, the login hero, table headers - is a token on the
 * theme, so no page has to hardcode a colour that only suits one of them.
 */

export type ThemeMode = 'light' | 'dark'
export type PaletteId = 'maroon' | 'indigo' | 'forest'

/** Colours the stock MUI palette has no home for, but several screens need. */
interface AppearanceTokens {
  /** Full-bleed gradient behind the sign-in card. */
  hero: string
  /** Table header fill, a shade off the paper surface. */
  tableHead: string
  /** Card and table cell outline. */
  outline: string
  /** Chart gridlines - fainter than the divider. */
  chartGrid: string
  /** Categorical chart series, in the order they should be handed out. */
  chartSeries: string[]
  /** Muted fill for "nothing here" states, e.g. an expired calendar hold. */
  neutral: string
}

interface PaletteTokens {
  primary: { main: string; light: string; dark: string; contrastText: string }
  secondary: { main: string; light: string; dark: string; contrastText: string }
  background: { default: string; paper: string }
  text: { primary: string; secondary: string }
  divider: string
  appearance: AppearanceTokens
}

declare module '@mui/material/styles' {
  interface Palette {
    appearance: AppearanceTokens
  }
  interface PaletteOptions {
    appearance?: AppearanceTokens
  }
}

/** Status hues are shared across palettes - green means paid everywhere. */
const STATUS: Record<ThemeMode, Record<'success' | 'warning' | 'error' | 'info', string>> = {
  light: { success: '#2e7d54', warning: '#c77b1a', error: '#b3261e', info: '#2c6d9e' },
  dark: { success: '#6fce9d', warning: '#e8ad5c', error: '#f28b82', info: '#79b8e8' },
}

const PALETTES: Record<PaletteId, Record<ThemeMode, PaletteTokens>> = {
  maroon: {
    light: {
      primary: { main: '#7a2030', light: '#a4485a', dark: '#54101d', contrastText: '#ffffff' },
      secondary: { main: '#b5883b', light: '#d9b169', dark: '#8a6220', contrastText: '#ffffff' },
      background: { default: '#f7f5f2', paper: '#ffffff' },
      text: { primary: '#241f1c', secondary: '#5f574f' },
      divider: '#e6e0d8',
      appearance: {
        hero: 'linear-gradient(140deg, #7a2030 0%, #4a121d 55%, #241f1c 100%)',
        tableHead: '#faf8f5',
        outline: '#e6e0d8',
        chartGrid: '#efeae3',
        chartSeries: ['#7a2030', '#b5883b', '#2c6d9e', '#2e7d54', '#c77b1a', '#8a6220'],
        neutral: '#bdb5aa',
      },
    },
    dark: {
      primary: { main: '#e08a99', light: '#f0b3bd', dark: '#b35a6b', contrastText: '#2a0f16' },
      secondary: { main: '#d9b169', light: '#ecd0a0', dark: '#a8813f', contrastText: '#2b2113' },
      background: { default: '#17110f', paper: '#1f1917' },
      text: { primary: '#f4efe9', secondary: '#b6aca2' },
      divider: '#332b27',
      appearance: {
        hero: 'linear-gradient(140deg, #4a121d 0%, #2b1218 55%, #100c0b 100%)',
        tableHead: '#241d1a',
        outline: '#332b27',
        chartGrid: '#2e2723',
        chartSeries: ['#e08a99', '#d9b169', '#79b8e8', '#6fce9d', '#e8ad5c', '#c39a63'],
        neutral: '#6d635b',
      },
    },
  },
  indigo: {
    light: {
      primary: { main: '#3c4b9a', light: '#6a78c2', dark: '#26326e', contrastText: '#ffffff' },
      secondary: { main: '#1c8a80', light: '#4fb5aa', dark: '#0f6259', contrastText: '#ffffff' },
      background: { default: '#f4f6fb', paper: '#ffffff' },
      text: { primary: '#171a24', secondary: '#565d6e' },
      divider: '#dfe3ee',
      appearance: {
        hero: 'linear-gradient(140deg, #3c4b9a 0%, #26326e 55%, #171a24 100%)',
        tableHead: '#f6f8fd',
        outline: '#dfe3ee',
        chartGrid: '#e8ebf4',
        chartSeries: ['#3c4b9a', '#1c8a80', '#c77b1a', '#8a5cc2', '#2c6d9e', '#b3466b'],
        neutral: '#b3bacb',
      },
    },
    dark: {
      primary: { main: '#9aa6f2', light: '#c0c8f8', dark: '#6b78c9', contrastText: '#131735' },
      secondary: { main: '#5cccc0', light: '#93e2da', dark: '#2c9a90', contrastText: '#06251f' },
      background: { default: '#101320', paper: '#181c2b' },
      text: { primary: '#eef0f8', secondary: '#a7aec4' },
      divider: '#2a3047',
      appearance: {
        hero: 'linear-gradient(140deg, #26326e 0%, #1a1f3d 55%, #0c0e18 100%)',
        tableHead: '#1d2233',
        outline: '#2a3047',
        chartGrid: '#262c40',
        chartSeries: ['#9aa6f2', '#5cccc0', '#e8ad5c', '#b79af0', '#79b8e8', '#e88fa8'],
        neutral: '#5b6480',
      },
    },
  },
  forest: {
    light: {
      primary: { main: '#2f5d3f', light: '#5b8a68', dark: '#1b3d28', contrastText: '#ffffff' },
      secondary: { main: '#a8791f', light: '#cfa451', dark: '#7c560f', contrastText: '#ffffff' },
      background: { default: '#f4f7f4', paper: '#ffffff' },
      text: { primary: '#1a211c', secondary: '#556058' },
      divider: '#dfe6df',
      appearance: {
        hero: 'linear-gradient(140deg, #2f5d3f 0%, #1b3d28 55%, #131a15 100%)',
        tableHead: '#f6f9f6',
        outline: '#dfe6df',
        chartGrid: '#e7eee7',
        chartSeries: ['#2f5d3f', '#a8791f', '#2c6d9e', '#7b4f9c', '#c05c3a', '#4e8f6d'],
        neutral: '#b4c0b6',
      },
    },
    dark: {
      primary: { main: '#7fc196', light: '#a9dcb9', dark: '#4d9169', contrastText: '#0d2416' },
      secondary: { main: '#d9ae63', light: '#ecd09a', dark: '#a37f37', contrastText: '#241a08' },
      background: { default: '#0f1611', paper: '#172119' },
      text: { primary: '#eaf2ec', secondary: '#a5b3a9' },
      divider: '#29372d',
      appearance: {
        hero: 'linear-gradient(140deg, #1b3d28 0%, #12291b 55%, #0a0f0b 100%)',
        tableHead: '#1b271e',
        outline: '#29372d',
        chartGrid: '#233028',
        chartSeries: ['#7fc196', '#d9ae63', '#79b8e8', '#b79af0', '#e8926d', '#6fce9d'],
        neutral: '#55665b',
      },
    },
  },
}

/** What the appearance picker lists, in display order. */
export const PALETTE_OPTIONS: { id: PaletteId; label: string; swatch: string }[] = [
  { id: 'maroon', label: 'Maroon', swatch: PALETTES.maroon.light.primary.main },
  { id: 'indigo', label: 'Indigo', swatch: PALETTES.indigo.light.primary.main },
  { id: 'forest', label: 'Forest', swatch: PALETTES.forest.light.primary.main },
]

export function isPaletteId(value: unknown): value is PaletteId {
  return value === 'maroon' || value === 'indigo' || value === 'forest'
}

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

/** Builds the MUI theme for one palette in one mode. Cheap enough to memoise per change. */
export function createAppTheme(paletteId: PaletteId, mode: ThemeMode): Theme {
  const tokens = PALETTES[paletteId][mode]
  const status = STATUS[mode]

  return createTheme({
    palette: {
      mode,
      primary: tokens.primary,
      secondary: tokens.secondary,
      success: { main: status.success },
      warning: { main: status.warning },
      error: { main: status.error },
      info: { main: status.info },
      background: tokens.background,
      text: tokens.text,
      divider: tokens.divider,
      appearance: tokens.appearance,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
      h4: { fontWeight: 650, letterSpacing: '-0.015em' },
      h5: { fontWeight: 650, letterSpacing: '-0.01em' },
      h6: { fontWeight: 620 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: `1px solid ${tokens.appearance.outline}` },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 650,
            backgroundColor: tokens.appearance.tableHead,
            whiteSpace: 'nowrap',
          },
          root: { borderColor: tokens.appearance.outline },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
      },
    },
  })
}

/**
 * Recharts renders raw SVG and never sees the MUI theme, so every chart has to
 * be handed its colours. One hook keeps them in step across all four dashboards.
 */
export function useChartTokens() {
  const theme = useTheme()
  return {
    grid: theme.palette.appearance.chartGrid,
    series: theme.palette.appearance.chartSeries,
    axis: theme.palette.text.secondary,
    /** Passed to <Tooltip contentStyle> - the default is white-on-white in dark mode. */
    tooltip: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.appearance.outline}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    },
  }
}

/** Colour a workflow status consistently wherever it appears. */
export function statusColor(
  status?: string,
): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' {
  if (!status) return 'default'
  switch (status.toUpperCase()) {
    case 'CONFIRMED':
    case 'APPROVED':
    case 'CUSTOMER_ACCEPTED':
    case 'PAID':
    case 'COMPLETED':
    case 'READY':
    case 'RESOLVED':
    case 'DONE':
    case 'PROCESSED':
      return 'success'
    case 'CANCELLED':
    case 'LOST':
    case 'CUSTOMER_REJECTED':
    case 'REJECTED':
    case 'FAILED':
    case 'OVERDUE':
    case 'REVERSED':
      return 'error'
    case 'TENTATIVE':
    case 'AWAITING_ADVANCE':
    case 'INTERNAL_APPROVAL':
    case 'PENDING':
    case 'PARTIALLY_PAID':
    case 'PARTIALLY_READY':
    case 'AWAITING_CUSTOMER':
    case 'ACCEPTED_AWAITING_CONFIRMATION':
      return 'warning'
    case 'SENT':
    case 'IN_PROGRESS':
    case 'PREPARATION_STARTED':
    case 'DISPATCHED':
    case 'PLANNING':
    case 'NEGOTIATION':
      return 'info'
    case 'CLOSED':
    case 'BILLED':
    case 'EXPIRED':
    case 'RETIRED':
      return 'default'
    default:
      return 'primary'
  }
}
